import { defineStore } from "pinia"
import type { Notification } from "~/apiclient/types.gen"
import { CentrifugeSubscriptionType } from "~/enums"

type NotificationsData = { items: Notification[]; unreadCount: number }
const NOTIF_KEY = ["notifications"]

export const useNotificationsStore = defineStore("notifications", () => {
	const { subscribe, unsubscribe } = useCentrifuge()
	const queryCache = useQueryCache()

	const onNotificationEvent = async (_payload: { event: string; data: unknown }) => {
		await queryCache.invalidateQueries({ key: NOTIF_KEY })
	}

	const subscribeToNotifications = () => {
		subscribe(onNotificationEvent, CentrifugeSubscriptionType.notification, "", () => {
			queryCache.invalidateQueries({ key: NOTIF_KEY })
		})
	}

	const unsubscribeFromNotifications = () => {
		unsubscribe("")
	}

	const markAllAsRead = async () => {
		await postUsersNotificationsRead()
		await queryCache.invalidateQueries({ key: NOTIF_KEY })
	}

	const markAsRead = async (id: string) => {
		const previous = queryCache.getQueryData<NotificationsData>(NOTIF_KEY)
		// Optimistic: flip the item and decrement the badge right away.
		queryCache.setQueryData<NotificationsData>(NOTIF_KEY, (old) => {
			if (!old) return old as unknown as NotificationsData
			const target = old.items.find((n) => n.id === id)
			if (!target || target.read) return old
			return {
				items: old.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
				unreadCount: Math.max(0, old.unreadCount - 1)
			}
		})
		try {
			await postUsersNotificationsByIdRead({ path: { id } })
		} catch (e) {
			// Roll back to the pre-update snapshot, then reconcile with the server.
			if (previous) queryCache.setQueryData<NotificationsData>(NOTIF_KEY, previous)
			await queryCache.invalidateQueries({ key: NOTIF_KEY })
			console.error("failed to mark notification as read: ", e)
		}
	}

	const clearRead = async () => {
		await deleteUsersNotificationsRead()
		await queryCache.invalidateQueries({ key: NOTIF_KEY })
	}

	return {
		markAllAsRead,
		markAsRead,
		clearRead,
		subscribeToNotifications,
		unsubscribeFromNotifications
	}
})
